import React, { useEffect, useRef, useState } from "react";
import { Invoice } from "../types";
import "./PDFViewer.css";
import { getCurrentFactorValue } from "../services/firebaseService";
import { Service } from "../types/service";
import { exportInvoiceToExcel } from "../utils/invoiceExcel";

interface PDFViewerProps {
  invoice?: Invoice;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ invoice }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [currentDate, setCurrentDate] = useState("");
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  useEffect(() => {
    const now = new Date();
    const formattedDate = `${now.getDate().toString().padStart(2, "0")}/${(now.getMonth() + 1).toString().padStart(2, "0")}/${now.getFullYear()} ${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
    setCurrentDate(formattedDate);
  }, []);

  // Debug: Log invoice data to understand structure
  useEffect(() => {
    if (invoice) {
      console.log("PDF Invoice full data:", JSON.stringify(invoice, null, 2));
      console.log("Invoice keys:", Object.keys(invoice));
      console.log("Photos direct access:", invoice.photos);
      console.log("Materials direct access:", invoice.materials);
      console.log("Photos type:", typeof invoice.photos);
      console.log("Materials type:", typeof invoice.materials);
    }
  }, [invoice]);

  const calculateTotalWithFactor = (
    services: Service[] | Record<string, Service> | null | undefined,
  ): number => {
    if (!services) return 0;

    const arr: Service[] = Array.isArray(services)
      ? services
      : Object.values(services);

    return arr.reduce((total, service) => {
      if (service?.price != null && service?.quantity != null) {
        const price = Number(service.price) || 0;
        const qty = Number(service.quantity) || 0;
        const svcFactor = Number(service.factor);
        const factor =
          Number.isFinite(svcFactor) && svcFactor > 0
            ? svcFactor
            : getCurrentFactorValue();
        return total + price * qty * factor;
      }
      return total;
    }, 0);
  };

  const servicesArray = React.useMemo(() => {
    if (!invoice?.services) return [];
    if (Array.isArray(invoice.services)) return invoice.services;

    const services = invoice.services as Record<string, Service>;

    return Object.keys(services)
      .sort((a, b) => {
        const na = Number(a),
          nb = Number(b);
        return Number.isFinite(na) && Number.isFinite(nb)
          ? na - nb
          : a.localeCompare(b);
      })
      .map((key) => ({ ...services[key], _key: key }));
  }, [invoice?.services]);

  const handlePrint = () => {
    window.print();
  };

  const handleExcelExport = async () => {
    if (!invoice || isExportingExcel) return;

    setIsExportingExcel(true);
    try {
      await exportInvoiceToExcel(invoice, getCurrentFactorValue());
    } catch (error) {
      console.error("Error exporting invoice to Excel:", error);
      window.alert("Não foi possível exportar o relatório para Excel.");
    } finally {
      setIsExportingExcel(false);
    }
  };

  if (!invoice) {
    return (
      <div className="pdf-viewer-container">
        <div className="pdf-error">
          <h2>Nenhum boletim selecionado</h2>
          <p>Selecione um boletim na tela anterior para visualizar o PDF.</p>
        </div>
      </div>
    );
  }

  const total = calculateTotalWithFactor(invoice.services);

  return (
    <div className="pdf-viewer-container">
      <div className="pdf-actions no-print">
        <button
          onClick={handleExcelExport}
          className="pdf-btn download"
          disabled={isExportingExcel}
        >
          <i
            className={
              isExportingExcel ? "fas fa-spinner fa-spin" : "fas fa-file-excel"
            }
          ></i>
          {isExportingExcel ? "Exportando..." : "Exportar para Excel"}
        </button>
        <button onClick={handlePrint} className="pdf-btn print">
          <i className="fas fa-print"></i>
          Imprimir
        </button>
        <button onClick={() => window.close()} className="pdf-btn close">
          <i className="fas fa-times"></i>
          Fechar
        </button>
      </div>

      <div ref={printRef} className="pdf-content">
        <div className="pdf-header">
          <table className="header-table">
            <tbody>
              <tr>
                <td rowSpan={4} className="logo-cell">
                  <img
                    src="/orca.png"
                    alt="Orca Logo"
                    width="120"
                    height="80"
                  />
                </td>
                <td className="company-name">
                  <strong>ORCA INSTALAÇÕES ELÉTRICAS E CIVIL</strong>
                </td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>AV. ROBERTO SANTOS, 105, CENTRO</td>
                <td>
                  <strong>IMPRESSO:</strong> {currentDate}
                </td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>SENHOR DO BONFIM - BA</td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>CNPJ: 11.936.571/0001-59</td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="pdf-title">
          <table className="title-table">
            <tbody>
              <tr>
                <th>PLANILHA DE FATURAMENTO DE SERVIÇO DE LINHA VIVA</th>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="pdf-info">
          <table className="info-table-1">
            <tbody>
              <tr>
                <td className="label-cell">
                  <strong>CONTRATO:</strong>
                </td>
                <td className="value-cell">4600035150</td>
                <td className="label-cell">
                  <strong>PLANILHA:</strong>
                </td>
                <td className="value-cell" colSpan={4}>
                  {invoice.id}
                </td>
              </tr>
              <tr>
                <td className="label-cell">
                  <strong>TÍTULO:</strong>
                </td>
                <td className="value-cell left-align" colSpan={5}>
                  {invoice.header.title || invoice.title}
                </td>
              </tr>
              <tr>
                <td className="label-cell">
                  <strong>PROJETO:</strong>
                </td>
                <td className="value-cell left-align" colSpan={5}>
                  {invoice.header.project || invoice.project}
                </td>
              </tr>
              <tr>
                <td className="label-cell">
                  <strong>MUNICÍPIO:</strong>
                </td>
                <td className="value-cell left-align" colSpan={5}>
                  {invoice.header.city || invoice.city}
                </td>
              </tr>
            </tbody>
          </table>

          <table className="info-table-2">
            <tbody>
              <tr>
                <td className="label-cell">
                  <strong>DATA:</strong>
                </td>
                <td className="value-cell">
                  {invoice.header.date || invoice.date}
                </td>
                <td className="label-cell">
                  <strong>S.E:</strong>
                </td>
                <td className="value-cell">
                  {(invoice.header as any).se || ""}
                </td>
                <td className="label-cell">
                  <strong>OS:</strong>
                </td>
                <td className="value-cell">
                  {invoice.header.os || invoice.os}
                </td>
              </tr>
              <tr>
                <td className="label-cell">
                  <strong>SETOR:</strong>
                </td>
                <td className="value-cell" colSpan={5}>
                  {(invoice.header as any).sector || ""}
                </td>
              </tr>
              <tr>
                <td className="label-cell">
                  <strong>PES:</strong>
                </td>
                <td className="value-cell">
                  {(invoice.header as any).si || ""}
                </td>
                <td className="label-cell">
                  <strong>PMS:</strong>
                </td>
                <td className="value-cell">
                  {invoice.header.pms || invoice.pms}
                </td>
                <td className="label-cell">
                  <strong>ALI:</strong>
                </td>
                <td className="value-cell">
                  {(invoice.header as any).ali || ""}
                </td>
              </tr>
              <tr>
                <td className="label-cell">
                  <strong>KM INICIAL:</strong>
                </td>
                <td className="value-cell">
                  {(invoice.header as any).kmInit || ""}
                </td>
                <td className="label-cell">
                  <strong>KM FINAL:</strong>
                </td>
                <td className="value-cell">
                  {(invoice.header as any).kmEnd || ""}
                </td>
                <td className="label-cell">
                  <strong>EQUIPE:</strong>
                </td>
                <td className="value-cell">
                  {(invoice.header as any).team || invoice.team}
                </td>
              </tr>
              <tr>
                <td className="label-cell">
                  <strong>RESP. COELBA:</strong>
                </td>
                <td className="value-cell" colSpan={2}>
                  {(invoice.header as any).respCoelba || ""}
                </td>
                <td className="label-cell">
                  <strong>RESP. ORCA:</strong>
                </td>
                <td className="value-cell" colSpan={2}>
                  {(invoice.header as any).respOrca || ""}
                </td>
              </tr>
              <tr>
                <td className="label-cell">
                  <strong>OPER./H.REC.:</strong>
                </td>
                <td className="value-cell" colSpan={2}>
                  {(invoice.header as any).operHRec || ""}
                </td>
                <td className="label-cell">
                  <strong>OPER./H.DEVOL.:</strong>
                </td>
                <td className="value-cell" colSpan={2}>
                  {(invoice.header as any).operHDev || ""}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="pdf-services">
          <table className="services-table">
            <thead>
              <tr>
                <th>CÓDIGO</th>
                <th>SERVIÇO</th>
                <th>QTD.</th>
                <th>VL.UNIT.</th>
                <th>UN.</th>
                <th>FATOR</th>
                <th>TOTAL PARCIAL</th>
              </tr>
            </thead>
            <tbody>
              {servicesArray.map((service: any, index: number) => {
                if (!service) return null;

                const price = Number(service.price) || 0;
                const qty = Number(service.quantity) || 0;
                const svcFactor = Number(service.factor);
                const defaultFactor = Number(getCurrentFactorValue()) || 1;
                const factor =
                  Number.isFinite(svcFactor) && svcFactor > 0
                    ? svcFactor
                    : defaultFactor;
                const partialTotal = (price * qty * factor).toFixed(2);

                const displayCode =
                  service.paid && service.pedido
                    ? `${service.code} (${service.pedido})`
                    : service.code;

                return (
                  <tr key={service._key ?? index}>
                    <td>{displayCode}</td>
                    <td>{service.activity || service.description}</td>
                    <td>{service.quantity}</td>
                    <td>{service.price}</td>
                    <td>{service.unid || "UN"}</td>
                    <td>{factor}</td>
                    <td>{partialTotal}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} className="total-label">
                  <strong>TOTAL</strong>
                </td>
                <td className="total-value">
                  <strong>{total.toFixed(2)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="pdf-observation">
          <table className="obs-table">
            <thead>
              <tr>
                <th>OBSERVAÇÃO:</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="obs-content">
                  {(invoice.header as any).observation ||
                    "Nenhuma observação informada."}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="pdf-photos">
          <div className="page-break"></div>
          <table className="photo-title-table">
            <thead>
              <tr>
                <th>Estruturas</th>
              </tr>
            </thead>
          </table>

          <div className="photos-grid">
            {(() => {
              const rawPhotos = invoice.photos;
              const photos = Array.isArray(rawPhotos)
                ? (rawPhotos as any[]).filter((p) => p && typeof p === "object")
                : [];

              if (!photos || photos.length === 0) {
                return (
                  <div className="no-photos">
                    <p>Nenhuma estrutura foi registrada para este boletim.</p>
                  </div>
                );
              }

              return photos.map((photo: any, index: number) => {
                const safePhoto = photo || {};
                return (
                  <div key={index} className="photo-item">
                    <table className="photo-table">
                      <tbody>
                        <tr>
                          <td rowSpan={4} className="photo-image">
                            <img
                              src={safePhoto.firebasePath || "/placeholder.jpg"}
                              alt={`Estrutura ${index + 1}`}
                              width="300"
                              height="250"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  "/placeholder.jpg";
                              }}
                            />
                          </td>
                          <td className="photo-label">
                            <strong>PG:</strong>
                          </td>
                        </tr>
                        <tr>
                          <td className="photo-pg">{safePhoto.pg || ""}</td>
                        </tr>
                        <tr>
                          <td className="photo-label">
                            <strong>OBSERVAÇÕES DO PG:</strong>
                          </td>
                        </tr>
                        <tr>
                          <td className="photo-obs">{safePhoto.obs || ""}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        <div className="pdf-materials">
          <table className="materials-title-table">
            <thead>
              <tr>
                <th>Materiais</th>
              </tr>
            </thead>
          </table>

          <table className="materials-table">
            <thead>
              <tr>
                <th>DESCRIÇÃO</th>
                <th>CÓDIGO (MATERIAL)</th>
                <th>QUANTIDADE</th>
                <th>AÇÃO</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const rawMaterials = invoice.materials;
                const materials = Array.isArray(rawMaterials)
                  ? (rawMaterials as any[]).filter(
                      (m) => m && typeof m === "object",
                    )
                  : [];

                if (materials.length === 0) {
                  return (
                    <tr>
                      <td colSpan={4} className="no-materials">
                        Nenhum material foi registrado para este boletim.
                      </td>
                    </tr>
                  );
                }

                return materials.map((material: any, index: number) => {
                  const safeMat = material || {};
                  return (
                    <tr key={index}>
                      <td>{safeMat.descriptions || ""}</td>
                      <td>{safeMat.material || ""}</td>
                      <td>{safeMat.quantity || ""}</td>
                      <td>{safeMat.action || ""}</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
